import React, { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getCrmOrgs } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2 } from 'lucide-react';
import { Eye, EyeSlash, ArrowRight, ShieldCheck, ArrowLeft } from '@phosphor-icons/react';
import { ParallaxScene, ParallaxLayer } from '../components/motion/ParallaxScene';
import { TiltCard } from '../components/motion/TiltCard';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_objectif-ecosystem/artifacts/4tz74flx_LOGO%20OBJECTIF%20SCOP%20OUTREMER%202%20%283%29.png';
const BG_URL = 'https://images.unsplash.com/photo-1748810859309-b943147cdda2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxDYXJpYmJlYW4lMjBpc2xhbmQlMjBwYXJhZGlzZSUyMGFlcmlhbCUyMGx1c2glMjBncmVlbiUyMHRyb3BpY2FsJTIwY29hc3RsaW5lfGVufDB8fHx8MTc3NTI3MzAzNnww&ixlib=rb-4.1.0&q=85';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendState, setResendState] = useState('idle'); // idle | sending | sent
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleResend = async () => {
    if (!email) return;
    setResendState('sending');
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/resend-verification`, { email });
      setResendState('sent');
    } catch {
      setResendState('sent');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNeedsVerification(false);
    setResendState('idle');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      // Check if user has multiple orgs
      try {
        const orgsRes = await getCrmOrgs();
        const orgsList = orgsRes.data.items || [];
        if (orgsList.length > 1) {
          navigate('/select-org');
        } else {
          if (orgsList.length === 1) {
            localStorage.setItem('crm_org_id', orgsList[0].id);
          }
          navigate('/dashboard');
        }
      } catch {
        navigate('/dashboard');
      }
    } else {
      const msg = result.error || '';
      // Détection : email non confirmé (backend renvoie « Veuillez d'abord confirmer votre adresse email »)
      if (/confirmer.*(votre|adresse).*email/i.test(msg) || /email.*non.*confirm/i.test(msg)) {
        setNeedsVerification(true);
      }
      setError(msg);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Outremer Visual */}
      <ParallaxScene className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background island image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${BG_URL}')` }}
        />
        {/* Gradient overlay with brand colors */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(26,36,33,0.75) 0%, rgba(45,90,76,0.6) 40%, rgba(0,174,239,0.35) 100%)'
        }} />
        {/* Decorative colored dots — animés en parallaxe superposée */}
        <ParallaxLayer depth={0.08} className="parallax-blob parallax-blob-cyan"    style={{ top: '8%',   right: '12%', width: 220, height: 220 }} />
        <ParallaxLayer depth={0.06} className="parallax-blob parallax-blob-lime"    style={{ top: '28%',  right: '4%',  width: 160, height: 160 }} />
        <ParallaxLayer depth={0.10} className="parallax-blob parallax-blob-orange" style={{ bottom: '30%', right: '18%', width: 180, height: 180 }} />
        <ParallaxLayer depth={0.05} className="parallax-blob parallax-blob-magenta" style={{ bottom: '10%', left: '10%', width: 140, height: 140 }} />

        {/* Content over image */}
        <div className="relative z-10 flex flex-col justify-between p-10 w-full">
          {/* Logo at top */}
          <div className="flex items-center gap-3 motion-fade-up motion-fade-up-delay-0">
            <img
              src={LOGO_URL}
              alt="Objectif SCOP Outremer"
              className="w-14 h-14 object-contain drop-shadow-lg"
              data-testid="login-desktop-logo"
            />
          </div>

          {/* Center visual accent */}
          <div className="flex-1 flex items-center justify-center">
          </div>

          {/* Bottom text */}
          <div className="motion-fade-up motion-fade-up-delay-2">
            <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope' }}>
              CRM ESS Outremer
            </h1>
            <p className="text-white/80 text-base max-w-sm leading-relaxed">
              Pilotez l'ecosysteme de l'Economie Sociale et Solidaire dans les territoires d'Outre-mer.
            </p>
            {/* Colored accent bar */}
            <div className="flex gap-1 mt-5">
              <div className="h-1 w-8 rounded-full" style={{ background: '#00AEEF' }} />
              <div className="h-1 w-8 rounded-full" style={{ background: '#80C51F' }} />
              <div className="h-1 w-8 rounded-full" style={{ background: '#EA3C3A' }} />
              <div className="h-1 w-8 rounded-full" style={{ background: '#FF8E00' }} />
              <div className="h-1 w-8 rounded-full" style={{ background: '#D5007D' }} />
            </div>
          </div>
        </div>
      </ParallaxScene>

      {/* Right side - Form ULTRA CHIC */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-10 relative overflow-hidden bg-[#F9F8F5]">
        {/* Ambient gradient background */}
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(1200px 600px at 100% 0%, rgba(45,90,76,0.10), transparent 60%), radial-gradient(800px 400px at 0% 100%, rgba(0,174,239,0.09), transparent 60%), linear-gradient(180deg, #F9F8F5 0%, #F3F1EA 100%)'
        }} />
        {/* Subtle grid texture */}
        <div className="absolute inset-0 opacity-[0.035] pointer-events-none" style={{
          backgroundImage: 'linear-gradient(#1A2421 1px, transparent 1px), linear-gradient(90deg, #1A2421 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} />
        <ParallaxLayer depth={0.04} className="parallax-blob parallax-blob-forest" style={{ top: '-10%', right: '-10%', width: 280, height: 280, opacity: 0.15 }} />
        <ParallaxLayer depth={0.06} className="parallax-blob parallax-blob-cyan"    style={{ bottom: '-8%', left: '-6%', width: 240, height: 240, opacity: 0.12 }} />

        <TiltCard max={3} className="relative w-full max-w-md motion-fade-up motion-fade-up-delay-1">
          {/* Halo behind card */}
          <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-[#2D5A4C]/25 via-[#00AEEF]/15 to-[#FF8E00]/20 blur-2xl -z-10 opacity-70" aria-hidden />

          <div className="relative rounded-3xl bg-white/85 backdrop-blur-xl border border-white/60 shadow-[0_20px_70px_-25px_rgba(26,36,33,0.35)] p-8 md:p-10">
            {/* Top brand ribbon */}
            <div className="flex items-center justify-between mb-4">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-xs text-[#5E6D66] hover:text-[#2D5A4C] font-semibold transition-colors"
                data-testid="login-back-home"
              >
                <ArrowLeft size={14} weight="bold" /> Retour à l'accueil
              </Link>
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#5E6D66] bg-[#F5F4EF] border border-[#E8E6E1] rounded-full px-2.5 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#80C51F] animate-pulse" /> ESPACE SÉCURISÉ
              </span>
            </div>
            <div className="flex items-center gap-2 mb-8">
              <img src={LOGO_URL} alt="O'SCOP" className="w-11 h-11 object-contain drop-shadow-md" data-testid="login-logo" />
              <div className="leading-tight">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#5E6D66] font-bold">Objectif SCOP</p>
                <p className="text-[11px] text-[#2D5A4C] font-semibold">Outremer</p>
              </div>
            </div>

            <div className="mb-7">
              <h2 className="text-3xl md:text-4xl font-extrabold text-[#1A2421] tracking-tight" style={{ fontFamily: 'Manrope' }}>
                Bon retour<span className="text-[#2D5A4C]">.</span>
              </h2>
              <p className="text-[#5E6D66] text-sm mt-1">Connectez-vous pour piloter votre écosystème ESS.</p>
              <div className="flex gap-1 mt-4">
                <span className="h-[3px] w-8 rounded-full bg-[#00AEEF]" />
                <span className="h-[3px] w-6 rounded-full bg-[#80C51F]" />
                <span className="h-[3px] w-4 rounded-full bg-[#EA3C3A]" />
                <span className="h-[3px] w-3 rounded-full bg-[#FF8E00]" />
                <span className="h-[3px] w-2 rounded-full bg-[#D5007D]" />
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="p-3 bg-[#EA3C3A]/10 border border-[#EA3C3A]/25 rounded-xl text-[#EA3C3A] text-sm animate-in fade-in slide-in-from-top-1" data-testid="login-error">
                  {error}
                  {needsVerification && (
                    <div className="mt-3 pt-3 border-t border-[#EA3C3A]/20" data-testid="resend-verification-block">
                      {resendState === 'sent' ? (
                        <p className="text-[13px] text-[#2D5A4C] font-semibold flex items-center gap-1.5" data-testid="resend-verification-sent">
                          <ShieldCheck size={14} weight="fill" /> Un nouveau lien vient d'être envoyé. Vérifiez votre boîte mail (et les spams).
                        </p>
                      ) : (
                        <button
                          type="button"
                          onClick={handleResend}
                          disabled={!email || resendState === 'sending'}
                          className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#2D5A4C] hover:text-[#1E4136] underline underline-offset-4 disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="resend-verification-btn"
                        >
                          {resendState === 'sending' ? <Loader2 className="animate-spin" size={13} /> : <ArrowRight size={13} weight="bold" />}
                          Renvoyer l'email de confirmation
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-[11px] uppercase tracking-wider text-[#5E6D66] font-semibold">Email professionnel</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@exemple.com"
                  required
                  className="h-12 rounded-xl border-[#E8E6E1] bg-white/70 focus:border-[#2D5A4C] focus:ring-4 focus:ring-[#2D5A4C]/15 transition-all"
                  data-testid="login-email"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-[11px] uppercase tracking-wider text-[#5E6D66] font-semibold">Mot de passe</Label>
                  <Link to="/forgot-password" className="text-xs text-[#2D5A4C] hover:text-[#1E4136] font-medium underline-offset-4 hover:underline transition-colors" data-testid="forgot-password-link">
                    Oublié ?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="h-12 rounded-xl border-[#E8E6E1] bg-white/70 focus:border-[#2D5A4C] focus:ring-4 focus:ring-[#2D5A4C]/15 transition-all pr-11"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5E6D66] hover:text-[#1A2421] hover:bg-[#F5F4EF] rounded-md p-1 transition-colors"
                    data-testid="toggle-password-visibility"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="group w-full h-12 rounded-xl text-[15px] font-semibold text-white transition-all shadow-lg shadow-[#2D5A4C]/25 hover:shadow-xl hover:shadow-[#2D5A4C]/35 hover:-translate-y-0.5 motion-shine"
                style={{ background: 'linear-gradient(135deg, #2D5A4C 0%, #1E4136 100%)' }}
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Connexion…</>
                ) : (
                  <span className="inline-flex items-center">Se connecter <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" weight="bold" /></span>
                )}
              </Button>
            </form>

            <div className="my-6 flex items-center gap-3 text-[10px] uppercase tracking-widest text-[#8A968F]">
              <span className="h-px flex-1 bg-[#E8E6E1]" /> nouveau chez O'SCOP <span className="h-px flex-1 bg-[#E8E6E1]" />
            </div>

            <Link
              to="/register"
              className="block text-center w-full py-3 rounded-xl border border-[#E8E6E1] hover:border-[#2D5A4C]/40 hover:bg-[#F5F4EF] text-sm font-semibold text-[#1A2421] transition-all"
              data-testid="register-link"
            >
              Créer un compte coopérateur
            </Link>

            <div className="mt-6 flex items-center justify-center gap-4 text-[10px] text-[#8A968F]">
              <span className="inline-flex items-center gap-1"><ShieldCheck size={12} weight="fill" className="text-[#80C51F]" /> Chiffrement TLS 1.3</span>
              <span className="w-1 h-1 rounded-full bg-[#E8E6E1]" />
              <span>© {new Date().getFullYear()} O'SCOP Outremer</span>
            </div>
          </div>
        </TiltCard>
      </div>
    </div>
  );
}
